import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { RFQ } from '../../../../types/rfq';
import { getQuotePreview } from '../../../../api/client';
import { toast } from '../../../../utils/notifications';
import { queryKeys } from "../../../../api/queryKeys";

/**
 * The quote PDF: whether it is attached, its preview, and the bytes. A PDF
 * built from items that have since changed is never kept or sent.
 */
export function useQuotePdf(rfq: RFQ, open: boolean, allItemsValidated: boolean) {
  const queryClient = useQueryClient();
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfData, setPdfData] = useState<string | null>(null);
  const [attachPdf, setAttachPdf] = useState(false);
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);

  // Bumped by every quote-PDF request and by anything that makes a loading
  // PDF stale; a response lands only if its id is still the current one.
  const pdfRequestRef = useRef(0);

  // Reset only when the dialog goes from closed to open. Not on every rfq
  // change: the parent re-renders on window-focus refetches, realtime
  // invalidations and the excel auto-run's refetch, and resetting then
  // silently unticked "Attach Quote PDF" so the quote went without it.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    const justOpened = open && !wasOpenRef.current;
    wasOpenRef.current = open;
    if (!justOpened) return;
    setPdfPreviewOpen(false);
    setAttachPdf(false);
    setIsLoadingPdf(false);
    pdfRequestRef.current++; // drop a PDF still loading from the last opening
    setPdfData(null); // Reset PDF data when dialog opens
  }, [open]);

  // Function to generate a fresh PDF
  const generateFreshPdf = async () => {
    // Only the latest request may land: one started before the items changed
    // (or before the dialog was reopened) describes a quote that is gone.
    const requestId = ++pdfRequestRef.current;
    const isCurrent = () => requestId === pdfRequestRef.current;
    try {
      setIsLoadingPdf(true);
      const data = await getQuotePreview(rfq.rfq_id);
      if (!isCurrent()) return;
      setPdfData(data.pdf_data);

      if (data.was_updated) {
        // If the backend updated the quote, refresh the RFQ data
        queryClient.invalidateQueries({ queryKey: queryKeys.rfq(rfq.rfq_id) });
      }
    } catch (error) {
      if (!isCurrent()) return;
      console.error('Error generating PDF:', error);

      // Show a more specific error message based on the error type
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate PDF. Please try again.';

      toast({
        title: "Error Generating Quote Preview",
        description: errorMessage,
        severity: "error"
      });
    } finally {
      if (isCurrent()) setIsLoadingPdf(false);
    }
  };

  // Generate the PDF when the checkbox is checked (or the items become
  // validated again while it is)
  useEffect(() => {
    if (attachPdf && !pdfData && allItemsValidated) {
      generateFreshPdf();
    }
  }, [attachPdf, allItemsValidated]);

  // Generate it for the preview
  useEffect(() => {
    if (pdfPreviewOpen && !pdfData && allItemsValidated) {
      generateFreshPdf();
    }
  }, [pdfPreviewOpen]);

  // A PDF built from the old items is stale: drop it (and any still loading),
  // and if "Attach Quote PDF" is ticked build a fresh one, so the box stays
  // ticked and the quote never goes out with the old PDF or none at all.
  useEffect(() => {
    pdfRequestRef.current++;
    setPdfData(null);
    setIsLoadingPdf(false);
    if (attachPdf && allItemsValidated) {
      generateFreshPdf();
    }
  }, [rfq.items]);

  return { pdfData, attachPdf, setAttachPdf, isLoadingPdf, pdfPreviewOpen, setPdfPreviewOpen };
}
